//app/page.tsx
'use client';

import { Suspense, useState, useEffect } from 'react';
import { HeroBetween } from '@/components/component/home/hero-between';
import { HeroBottom } from '@/components/component/home/hero-bottom';
import { HeroMid } from '@/components/component/home/hero-mid';
import { MarketplacePreview } from '@/components/component/home/marketplace-preview';
import { HeroFront } from '@/components/component/home/hero-front';
import { Footer } from '@/components/component/footer';
import { SupportMain } from '@/components/component/support/support-main';
import { SupportButton } from '@/components/component/support/SupportButton';
import { DiscountCard } from '@/components/component/discount-card';
import { AnimatePresence, motion } from 'framer-motion';
import hexBackground from '../public/hex.svg';
import { Navbar1 } from '@/components/component/navbar/navbar';
import { useUI } from '@/context/UIContext';

export default function Home() {
  const [isSticky, setIsSticky] = useState(false);
  const [showSupportMain, setShowSupportMain] = useState(false);
  const { isProfileOpen } = useUI();

  const handleSupportButtonClick = () => {
    setShowSupportMain(!showSupportMain);
  };

  const handleSupportMainClose = () => {
    setShowSupportMain(false);
  };

  useEffect(() => {
    // Force a reflow to ensure styles are applied
    document.body.classList.add('home-page');

    return () => {
      document.body.classList.remove('home-page');
    };
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col overflow-hidden relative scrollable-content"
      style={{
        backgroundImage:
          'linear-gradient(to bottom, #0f172a, #1e293b, #0f172a)',
        filter: isProfileOpen ? undefined : 'contrast(110%) saturate(130%)',
      }}
    >
      {/* Enhanced background elements */}
      <div
        className="absolute top-0 left-0 w-full h-full bg-no-repeat pointer-events-none z-0 opacity-20"
        style={{
          backgroundImage:
            'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.4), transparent 70%)',
        }}
      />

      <div
        className="absolute top-10 -right-1/4 w-3/4 h-3/4 bg-no-repeat pointer-events-none z-0"
        style={{
          backgroundImage: `url(${hexBackground})`,
          backgroundPosition: 'top right',
          backgroundSize: 'contain',
          backgroundOrigin: 'content-box',
          padding: '10px',
          opacity: 0.15,
        }}
      />

      {/* Subtle moving particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 0%, transparent 10%)',
            backgroundSize: '25px 25px',
          }}
        />
      </div>

      {/* The discount card - highest z-index for proper overlay */}
      <div className="fixed inset-0 pointer-events-none z-[1000]">
        <Suspense fallback={null}>
          <DiscountCardWrapper />
        </Suspense>
      </div>

      <div
        className={`sticky top-0 z-40 ${
          isSticky
            ? 'bg-slate-900/70 backdrop-blur-xl shadow-lg shadow-blue-900/10 border-b border-blue-500/10'
            : ''
        } transition-all duration-300`}
      >
        <Navbar1 isSticky={isSticky} isTransparent={!isSticky} />
      </div>

      <motion.div
        className="relative z-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <HeroFront setIsSticky={setIsSticky} />
      </motion.div>

      <motion.div
        className="relative z-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <MarketplacePreview />
      </motion.div>

      <div className="relative z-10">
        <HeroMid />
        <HeroBetween />
        <HeroBottom />
        <Footer />
      </div>

      <div className="relative z-30">
        <AnimatePresence>
          {showSupportMain && <SupportMain onClose={handleSupportMainClose} />}
        </AnimatePresence>
      </div>

      <SupportButton
        onClick={handleSupportButtonClick}
        isVisible={!showSupportMain}
      />
    </div>
  );
}

function DiscountCardWrapper() {
  const [showDiscountCard, setShowDiscountCard] = useState(false);

  useEffect(() => {
    // Check for coupon parameter in URL hash
    const checkCouponParam = () => {
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const couponCode = hashParams.get('coupon');
      setShowDiscountCard(couponCode !== null);
    };

    // Check immediately
    checkCouponParam();

    // Also set up an event listener for hash changes
    window.addEventListener('hashchange', checkCouponParam);

    return () => {
      window.removeEventListener('hashchange', checkCouponParam);
    };
  }, []);

  const handleDiscountCardClose = () => {
    setShowDiscountCard(false);
    // Remove coupon parameter from URL
    window.history.pushState({}, document.title, window.location.pathname);
  };

  return (
    <div className={`pointer-events-${showDiscountCard ? 'auto' : 'none'}`}>
      <AnimatePresence>
        {showDiscountCard && <DiscountCard onClose={handleDiscountCardClose} />}
      </AnimatePresence>
    </div>
  );
}
